package com.bikompanion.domain.analytics

import com.bikompanion.domain.BikeIssue
import com.bikompanion.domain.BikeKey
import com.bikompanion.domain.BikeScopeSource
import com.bikompanion.domain.StationWatch
import com.bikompanion.domain.SubmitVoteResult

/**
 * Outcomes of domain mutations, recorded by the use cases that perform them.
 *
 * Station ids are left out on purpose: a watched station is often the one by a rider's home or
 * work, and the network is enough to see how the feature is used.
 */
object DomainAnalyticsEvents {
    fun bikeVoteSubmitted(
        key: BikeKey,
        isPositive: Boolean,
        isElectric: Boolean,
        issues: List<BikeIssue>,
        scopeSource: BikeScopeSource,
        result: SubmitVoteResult,
    ) = AnalyticsEvent(
        name = "bike_vote_submitted",
        parameters = buildMap {
            put("outcome", result.analyticsOutcome())
            put("positive", isPositive.analyticsValue())
            put("electric", isElectric.analyticsValue())
            put("issue_count", issues.size.toLong())
            if (issues.isNotEmpty()) put("issues", issues.joinToString(",") { it.analyticsValue() })
            put("network_id", key.networkId)
            put("scope_source", scopeSource.analyticsValue())
        },
    )

    fun stationWatchStarted(watch: StationWatch) = AnalyticsEvent("station_watch_started", watch.parameters())

    fun stationWatchCancelled(watch: StationWatch) = AnalyticsEvent("station_watch_cancelled", watch.parameters())

    fun stationWatchExpired(watch: StationWatch) = AnalyticsEvent("station_watch_expired", watch.parameters())

    fun stationWatchFound(
        watch: StationWatch,
        waitedMillis: Long,
    ) = AnalyticsEvent(
        name = "station_watch_found",
        parameters = watch.parameters() + ("waited_seconds" to waitedMillis.coerceAtLeast(0) / 1_000),
    )
}

private fun StationWatch.parameters(): Map<String, Any> =
    mapOf(
        "network_id" to networkId,
        "bike_type" to bikeType.analyticsValue(),
    )

private fun SubmitVoteResult.analyticsOutcome(): String =
    when (this) {
        SubmitVoteResult.Recorded -> "recorded"
        SubmitVoteResult.Queued -> "queued"
        is SubmitVoteResult.InCooldown -> "in_cooldown"
        is SubmitVoteResult.Failed -> "failed_${reason.analyticsValue()}"
    }
